import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X, ExternalLink, Phone } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';

const MedicalAlertBanner: React.FC = () => {
  const { alerts, clearAlert } = useNotifications();

  // Get the most critical unread alert
  const criticalAlert = alerts.find(alert => 
    alert.severity === 'critical' && 
    !alert.isRead && 
    alert.data?.urgency === 'urgent'
  );

  if (!criticalAlert) return null;

  const handleEmergencyCall = () => {
    // In a real app, this could open the phone app or provide emergency numbers
    window.open('tel:911', '_self');
  };

  const handleDismiss = () => {
    clearAlert(criticalAlert.id);
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed top-20 left-4 right-4 z-50 max-w-md mx-auto"
        initial={{ opacity: 0, y: -50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -50, scale: 0.9 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
      >
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 shadow-2xl backdrop-blur-sm">
          <div className="flex items-start space-x-3">
            <motion.div
              className="p-2 bg-red-500 rounded-full flex-shrink-0"
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            >
              <AlertTriangle className="w-5 h-5 text-white" />
            </motion.div>
            
            <div className="flex-1">
              <h3 className="font-bold text-red-900 mb-1">🚨 Medical Alert</h3>
              <p className="text-sm text-red-800 mb-3">{criticalAlert.message}</p>
              
              {criticalAlert.data?.recommendation && (
                <div className="p-2 bg-red-100 border border-red-200 rounded-lg mb-3">
                  <p className="text-xs text-red-900">
                    <strong>Immediate Action:</strong> {criticalAlert.data.recommendation}
                  </p>
                </div>
              )}
              
              <div className="flex space-x-2">
                <motion.button
                  onClick={handleEmergencyCall}
                  className="flex items-center space-x-1 px-3 py-2 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Phone className="w-3 h-3" />
                  <span>Emergency</span>
                </motion.button>
                
                <motion.button
                  onClick={() => window.open('https://www.healthline.com/health/womens-health', '_blank')}
                  className="flex items-center space-x-1 px-3 py-2 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ExternalLink className="w-3 h-3" />
                  <span>Learn More</span>
                </motion.button>
              </div>
            </div>
            
            <motion.button
              onClick={handleDismiss}
              className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <X className="w-4 h-4" />
            </motion.button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MedicalAlertBanner;